import { useMemo } from 'react';
import { cn } from '../lib/cn.js';

/** Palette from the design: deterministic per person, so avatars never shuffle. */
const PALETTE = ['#123B73', '#2563EB', '#0E7490', '#7C3AED', '#B45309', '#0F766E'] as const;

function hashName(name: string): number {
  let hash = 0;
  for (let index = 0; index < name.length; index++) {
    hash = (hash * 31 + name.charCodeAt(index)) >>> 0;
  }
  return hash;
}

export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase();
}

export function colorForName(name: string): string {
  return PALETTE[hashName(name) % PALETTE.length] as string;
}

export interface UserAvatarProps {
  name: string;
  /** Diameter in px. The design uses 20–32 in lists and 30–32 in chrome. */
  size?: number;
  imageUrl?: string;
  className?: string;
}

/**
 * Initials avatar with a colour derived from the name.
 * Falls back to initials whenever an image is missing or fails to load.
 */
export function UserAvatar({ name, size = 32, imageUrl, className }: UserAvatarProps) {
  const { initials, background, fontSize } = useMemo(
    () => ({
      initials: initialsOf(name),
      background: colorForName(name),
      fontSize: Math.max(10, Math.round(size * 0.38)),
    }),
    [name, size],
  );

  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full font-semibold text-white',
        className,
      )}
      style={{ width: size, height: size, minWidth: size, background, fontSize }}
      title={name}
    >
      {imageUrl ? (
        <img src={imageUrl} alt={name} className="h-full w-full object-cover" />
      ) : (
        <span aria-hidden="true">{initials}</span>
      )}
      {imageUrl ? null : <span className="sr-only">{name}</span>}
    </span>
  );
}

export interface AvatarWithNameProps extends UserAvatarProps {
  secondary?: string;
  nameClassName?: string;
}

/** Avatar plus name, the pairing used in lists, headers and the sidebar. */
export function AvatarWithName({
  name,
  secondary,
  size = 26,
  imageUrl,
  className,
  nameClassName,
}: AvatarWithNameProps) {
  return (
    <div className={cn('flex min-w-0 items-center gap-2', className)}>
      <UserAvatar name={name} size={size} imageUrl={imageUrl} />
      <div className="min-w-0">
        <div className={cn('truncate text-[12.5px] font-semibold text-ink', nameClassName)}>
          {name}
        </div>
        {secondary ? (
          <div className="truncate text-[11px] font-medium text-ink-faint">{secondary}</div>
        ) : null}
      </div>
    </div>
  );
}
